import React, { useState, useRef, createRef, useEffect } from "react";
import {
    Tabs,
    Button,
    Modal,
    Skeleton,
    Card,
    Space,
    Typography
} from "antd";
import {
    FilePdfOutlined,
    CheckCircleOutlined,
    EditOutlined,
    SendOutlined,
    PrinterOutlined,
    StopOutlined,
    RollbackOutlined,
    HistoryOutlined
} from "@ant-design/icons";
import DTRFFormat from "../../components/docs/DTRFFormat";
import DTRFFormatProposition from "../../components/docs/DTRFFormatProposition";
import DTRFWPPFormat from "../../components/docs/DTRFWPPFormat";
import DTRFWPPFormatToPrint from "../../components/docs/DTRFWPPFormatToPrint";
import AttestationMotoToPrint from "../../components/docs/AttestationMotoToPrint";
import MouvementsContent from "./MouvementContent";
import ImprimerAttestation from "./ImprimerAttestation";
import ImprimerAttestationCEDEAO from "./ImprimerAttestationCEDEAO";
import HistoriqueAttestationsContent from "./HistoriqueAttestationsContent";

const { Title, Text } = Typography;

const DetailsTab = ({
    contrat,
    reglements,
    loading,
    regle,
    printingCP,
    loadingPrinting,
    printReference,
    printRefContrat,
    printRefAttestation,
    printedAttestation,
    attestationNodeRefs,
    printedCEDEAOAttestation,
    contratModal,
    openAvenantModifier,
    avenantModalOpen,
    modalAttOpen,
    modalCEDEAOAttOpen,
    numeroAttestation,
    attestationDamaged,
    printingAttestation,
    printAttestation,
    printingCEDEAOAttestation,
    pdfPreviewUrl,
    tabId,
    onTabsChange,
    setModalAttOpen,
    setModalCEDEAOAttOpen,
    setNumeroAttestation,
    setAttestationDamaged,
    setPrintingAttestation,
    setPrintingCEDEAOAttestation,
    setContratModal,
    exportToPdf,
    exportToOnePdf,
    showContractValidationConfirm,
    showCancelAvenantConfirm,
    showCancelValidationConfirm,
    showCancelPropositionConfirm,
    showRestorePropositionConfirm,
    reglementWarningWithConfirmAndCancelMessage,
    attestationPrinting,
    printAllAttestations,
    printAllAttestationsRef,
    awaitingBulkConfirm,
    confirmBulkPrintSuccess,
    retryBulkPrint,
    abortBulkPrint,
    attestationDamagedPrinting,
    attestationNonPrintedPrinting,
    attestationCEDEAOPrinting,
    attestationCEDEAODamagedPrinting,
    attestationCEDEAONonPrintedPrinting,
    printingDamagedAttestation,
    printingDamagedCEDEAOAttestation,
    rightsService,
    navigate,
    layout,
    setLayout,
    setLoadingPrinting,
    showAttestationCEDEAO
}) => {

    // Loader sur le bouton « Modifier » (avenant en proposition) en attendant l'ouverture de la modale
    const [loadingModifier, setLoadingModifier] = useState(false);
    useEffect(() => {
        if (avenantModalOpen) setLoadingModifier(false);
    }, [avenantModalOpen]);
    // Avenants disposant de l'édition EN PLACE d'une proposition (le bouton Modifier les concerne).
    // AG désormais éditable en place (augmentation-garantie/proposition). DG reste sans édition.
    const MODIFIABLE_AVENANT_CODES = ["RN", "PR", "TG", "RV", "IN", "AG"];
    const isModifiableAvenant = MODIFIABLE_AVENANT_CODES.includes(contrat?.avenant?.code);

    // Duplicata (AD) : cet avenant ne crée PAS de version de contrat (juste une ligne de règlement),
    // donc contrat.avenant n'est jamais "AD". On détecte sa présence via les mouvements/règlements
    // (payé OU non) et on estampille le contrat transmis aux documents → filigrane « DUPLICATA »
    // sur la CP et les attestations (cf. DuplicataStamp).
    // SCOPÉ À LA VERSION : le règlement AD porte (via contratCodeId) la version qu'il duplique. On ne
    // marque DUPLICATA que si l'AD concerne LA version affichée — sinon le filigrane fuiterait sur les
    // avenants ULTÉRIEURS (ex. une incorporation créée après le duplicata).
    const aDuplicata = Array.isArray(reglements) && reglements.some((r) =>
        r?.avenant?.code === "AD" && (r?.contratCodeId || r?.contrat?.codeId) === contrat?.codeId);
    const contratDoc = contrat ? { ...contrat, aDuplicata } : contrat;
    // Souscription pour l'État (HCO106) : autorise l'impression CP + attestations SANS encaissement
    // (l'État paie sous 6 mois). N'affecte QUE l'activation de l'impression, pas la logique `!regle`.
    const canPrintEtat = !!contrat?.contratEtat && rightsService("HCO106");

    // AVENANT TARIFANT (OE) NON ENCAISSÉ : un OE validé dont la prime ≠ 0 génère un règlement à
    // encaisser (visible dans /en-attente-d-encaissement) mais N'APPARAÎT PAS encore dans les
    // mouvements tant qu'il n'est pas passé en caisse (aucun règlement SOLDÉ porté par CETTE version).
    // `regle` ne le détecte pas (il n'existe pas de règlement INCOMPLET, juste PAS de règlement) → il
    // faut interdire l'impression de la CP tant que l'OE n'est pas réglé (il doit passer en caisse avant).
    const versionRegleeEnCaisse = Array.isArray(reglements) && reglements.some((r) =>
        (r?.contratCodeId || r?.contrat?.codeId) === contrat?.codeId
        && !r?.annule
        && (r?.dateReglement || r?.modePaiement));
    // AVENANT D'ANNULATION (AA) : la CP s'imprime AVANT le règlement de la ristourne — l'assuré la
    // présente en caisse comme justificatif pour toucher sa ristourne.
    const cpAvantReglement = contrat?.statutContrat === "VALIDE" && contrat?.avenant?.code === "AA";
    const avenantPrimeNonRegle = contrat?.statutContrat === "VALIDE"
        && contrat?.avenant?.code === "OE"
        && Math.abs(Number(contrat?.primeTotale) || 0) > 0.0001
        && !versionRegleeEnCaisse;

    const primaryColor = "#1890ff";
    const secondaryColor = "#242166";
    const accentColor = "#52c41a";

    const cardStyle = {
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: `1px solid ${secondaryColor}15`
    };

    const buttonStyle = {
        borderRadius: "8px",
        height: "40px",
        fontWeight: 500,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    };

    const tabItems = [
        {
            key: "1",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Contrat</Text>
                </Space>
            ),
            children: (
                <Card style={cardStyle} bodyStyle={{ padding: "32px" }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "32px",
                        background: "#fafafa",
                        padding: "24px",
                        borderRadius: "8px"
                    }}>
                        <div style={{ width: "210mm", height: "auto" }}>
                            {contrat && (contrat?.statutContrat === "EN_PROPOSITION" ? (
                                <DTRFFormatProposition contrat={contratDoc} ref={printReference} />
                            ) : (contrat?.engins?.[0]?.categorie?.codeCategorie === "M" &&
                                contrat?.organime?.typeOrganisme === "POOL" ?
                                !printingCP ?
                                    <DTRFWPPFormat contrat={contratDoc} ref={printReference} /> :
                                    <DTRFWPPFormatToPrint contrat={contratDoc} ref={printReference} /> :
                                <DTRFFormat contrat={contratDoc} ref={printReference} />
                            ))}
                        </div>
                    </div>

                    <Space wrap size="large" style={{ justifyContent: "center", width: "100%" }}>
                        {contrat?.statutContrat === "EN_PROPOSITION" ? (
                            <>
                                {/* Proposition EN CORBEILLE : on n'affiche que la restauration (elle reste
                                    une proposition, on ne la valide/modifie/annule pas tant qu'elle est en corbeille). */}
                                {contrat?.corbeille && (
                                    <Button
                                        onClick={showRestorePropositionConfirm}
                                        type="primary"
                                        icon={<RollbackOutlined />}
                                        style={{
                                            ...buttonStyle,
                                            background: accentColor,
                                            borderColor: accentColor
                                        }}
                                        size="large"
                                    >
                                        Restaurer la proposition
                                    </Button>
                                )}
                                {!contrat?.corbeille && (
                                    <Button
                                        onClick={showContractValidationConfirm}
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        disabled={!rightsService("HCO003")}
                                        style={{
                                            ...buttonStyle,
                                            background: accentColor,
                                            borderColor: accentColor
                                        }}
                                        size="large"
                                    >
                                        Valider le Contrat
                                    </Button>
                                )}
                                {/* Affaire nouvelle → édition via page dédiée. Avenants modifiables → modale.
                                    AG/DG/IN : pas d'édition en place (pas de bouton Modifier). */}
                                {!contrat?.corbeille && (!contrat?.avenant?.code?.startsWith("AN") ? isModifiableAvenant : true) && (
                                    <Button
                                        onClick={() => {
                                            if (isModifiableAvenant) {
                                                setLoadingModifier(true);
                                                setTimeout(() => { openAvenantModifier && openAvenantModifier(); }, 50);
                                                setTimeout(() => setLoadingModifier(false), 3000);
                                            } else {
                                                navigate(`/editer-contrat-new/${contrat?.codeId}`);
                                            }
                                        }}
                                        loading={loadingModifier}
                                        icon={<EditOutlined />}
                                        disabled={!rightsService("HCO003")}
                                        style={buttonStyle}
                                        size="large"
                                    >
                                        {loadingModifier ? "Ouverture…" : "Modifier"}
                                    </Button>
                                )}
                                <Button
                                    loading={loadingPrinting}
                                    onClick={exportToPdf}
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    disabled={!rightsService("HLA044")}
                                    style={{
                                        ...buttonStyle,
                                        background: secondaryColor,
                                        borderColor: secondaryColor
                                    }}
                                    size="large"
                                >
                                    Imprimer la Proposition
                                </Button>
                                {/* Annulation d'une proposition (mise en corbeille, non destructive,
                                    restaurable). Disponible aussi depuis la liste par sélection multiple. */}
                                {!contrat?.corbeille && (
                                    <Button
                                        onClick={showCancelPropositionConfirm}
                                        danger
                                        icon={<StopOutlined />}
                                        disabled={!rightsService("HCO003")}
                                        style={buttonStyle}
                                        size="large"
                                    >
                                        {(contrat?.avenant?.code && !contrat.avenant.code.toUpperCase().startsWith("AN"))
                                            ? "Annuler l'avenant" : "Annuler la proposition"}
                                    </Button>
                                )}
                            </>
                        ) : ((regle || canPrintEtat || cpAvantReglement) && !avenantPrimeNonRegle) ? (
                            <Button
                                loading={loadingPrinting}
                                onClick={exportToPdf}
                                type="primary"
                                icon={<FilePdfOutlined />}
                                disabled={!rightsService("HCO006")}
                                style={{
                                    ...buttonStyle,
                                    background: accentColor,
                                    borderColor: accentColor
                                }}
                                size="large"
                            >
                                Imprimer le Contrat
                            </Button>
                        ) : contrat?.avenant?.code?.startsWith("AN") ? (
                            <Button
                                onClick={() => navigate(`/editer-contrat-new/${contrat?.codeId}`)}
                                icon={<EditOutlined />}
                                disabled={!rightsService("HCO003")}
                                style={buttonStyle}
                                size="large"
                            >
                                Modifier le Contrat
                            </Button>
                        ) : null}

                        {/* Annuler le DERNIER avenant (le contrat affiché) : visible si l'avenant est
                            VALIDÉ, NON réglé, n'est pas une affaire nouvelle, et droit HCO082.
                            (L'annulation d'un avenant plus ancien se fait dans « Historique des
                            mouvements » sur sa ligne de règlement.) */}
                        {contrat?.statutContrat === "VALIDE"
                            && (!regle || avenantPrimeNonRegle)
                            && contrat?.avenant?.code !== "AN"
                            && rightsService("HCO082") && (
                                <Button
                                    onClick={showCancelAvenantConfirm}
                                    danger
                                    icon={<StopOutlined />}
                                    style={buttonStyle}
                                    size="large"
                                >
                                    Annuler l'avenant
                                </Button>
                            )}

                        {/* Annuler la VALIDATION d'une affaire nouvelle (AN) : ramène le contrat à
                            l'état Proposition. Visible si VALIDÉ, NON encaissé, affaire nouvelle, et
                            droit HCO082. Le backend bloque l'opération si un encaissement existe déjà. */}
                        {contrat?.statutContrat === "VALIDE"
                            && !regle
                            && contrat?.avenant?.code === "AN"
                            && rightsService("HCO082") && (
                                <Button
                                    onClick={showCancelValidationConfirm}
                                    danger
                                    icon={<RollbackOutlined />}
                                    style={buttonStyle}
                                    size="large"
                                >
                                    Annuler la validation
                                </Button>
                            )}

                        {contrat?.organisme?.typeOrgainsme === "POOL" && (
                            <Button
                                onClick={() => setContratModal(true)}
                                type="primary"
                                icon={<SendOutlined />}
                                disabled={(regle && !rightsService("HCO006")) || (!regle && !rightsService("HLA037"))}
                                style={{
                                    ...buttonStyle,
                                    background: primaryColor,
                                    borderColor: primaryColor
                                }}
                                size="large"
                            >
                                Envoyer les Documents
                            </Button>
                        )}
                    </Space>
                </Card>
            )
        },
        {
            key: "2",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Mouvements</Text>
                </Space>
            ),
            children: (
                <MouvementsContent
                    contrat={contrat}
                    reglements={reglements}
                    rightsService={rightsService}
                />
            )
        },
        {
            key: "3",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Attestation</Text>
                </Space>
            ),
            children: null // Géré par le modal
        },
        {
            key: "4",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Carte Grise</Text>
                </Space>
            ),
            children: (
                <Card style={cardStyle} bodyStyle={{ padding: "32px" }}>
                    {contrat?.engins?.[0]?.carteGrise ? (
                        <div style={{ height: "750px", borderRadius: "8px", overflow: "hidden" }}>
                            <embed
                                src={pdfPreviewUrl}
                                type="application/pdf"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "60px",
                            color: "#999"
                        }}>
                            <Text type="secondary">Pas de carte grise jointe au contrat</Text>
                        </div>
                    )}
                </Card>
            )
        },
        {
            key: "hist-attestations",
            label: (
                <Space>
                    <HistoryOutlined />
                    <Text strong>Historique attestations</Text>
                </Space>
            ),
            children: <HistoriqueAttestationsContent contrat={contrat} />
        }
    ];

    if (showAttestationCEDEAO) {
        tabItems.push({
            key: "5",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Attestation CEDEAO</Text>
                </Space>
            ),
            children: null // Géré par le modal
        });
    }

    return (
        <>
            <Skeleton loading={loading} active>
                <Tabs
                    onChange={onTabsChange}
                    items={tabItems}
                    activeKey={tabId}
                    size="large"
                    style={{
                        background: "white",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                    }}
                    tabBarStyle={{
                        borderBottom: `2px solid ${secondaryColor}20`,
                        marginBottom: "24px"
                    }}
                />
            </Skeleton>

            {/* Modal Attestation */}
            <ImprimerAttestation
                modalAttOpen={modalAttOpen}
                contrat={contratDoc}
                onTabsChange={onTabsChange}
                setModalAttOpen={setModalAttOpen}
                printingAttestation={printingAttestation}
                setPrintingAttestation={setPrintingAttestation}
                printedAttestation={printedAttestation}
                attestationNodeRefs={attestationNodeRefs}
                printAttestation={printAttestation}
                numeroAttestation={numeroAttestation}
                setNumeroAttestation={setNumeroAttestation}
                attestationDamaged={attestationDamaged}
                setAttestationDamaged={setAttestationDamaged}
                attestationPrinting={attestationPrinting}
                printAllAttestations={printAllAttestations}
                printAllAttestationsRef={printAllAttestationsRef}
                awaitingBulkConfirm={awaitingBulkConfirm}
                confirmBulkPrintSuccess={confirmBulkPrintSuccess}
                retryBulkPrint={retryBulkPrint}
                abortBulkPrint={abortBulkPrint}
                attestationDamagedPrinting={attestationDamagedPrinting}
                attestationNonPrintedPrinting={attestationNonPrintedPrinting}
                printingDamagedAttestation={printingDamagedAttestation}
                regle={regle || canPrintEtat}
                loadingPrinting={loadingPrinting}
                setLoadingPrinting={setLoadingPrinting}
                rightsService={rightsService}
                layout={layout}
                setLayout={setLayout}
            />

            {/* Modal CEDEAO */}
            {showAttestationCEDEAO && (
                <ImprimerAttestationCEDEAO
                    modalCEDEAOAttOpen={modalCEDEAOAttOpen}
                    contrat={contratDoc}
                    onTabsChange={onTabsChange}
                    setModalCEDEAOAttOpen={setModalCEDEAOAttOpen}
                    printingCEDEAOAttestation={printingCEDEAOAttestation}
                    setPrintingCEDEAOAttestation={setPrintingCEDEAOAttestation}
                    printedCEDEAOAttestation={printedCEDEAOAttestation}
                    printingDamagedCEDEAOAttestation={printingDamagedCEDEAOAttestation}
                    numeroAttestation={numeroAttestation}
                    setNumeroAttestation={setNumeroAttestation}
                    attestationCEDEAOPrinting={attestationCEDEAOPrinting}
                    attestationCEDEAODamagedPrinting={attestationCEDEAODamagedPrinting}
                    attestationCEDEAONonPrintedPrinting={attestationCEDEAONonPrintedPrinting}
                    attestationDamaged={attestationDamaged}
                    setAttestationDamaged={setAttestationDamaged}
                    regle={regle || canPrintEtat}
                    rightsService={rightsService}
                />
            )}

            {/* Modal Documents Pool */}
            <Modal
                title={
                    <Title level={4} style={{ margin: 0, color: secondaryColor }}>
                        <SendOutlined style={{ marginRight: 8 }} />
                        Envoyer les Documents
                    </Title>
                }
                open={contratModal}
                onCancel={() => setContratModal(false)}
                footer={null}
                width={1200}
                centered
                bodyStyle={{ padding: "24px" }}
                zIndex={2000}
            >
                <Card style={{ ...cardStyle, marginBottom: 24 }}>
                    <div style={{ background: "#fafafa", padding: "24px", borderRadius: "8px" }}>
                        <div style={{ width: "210mm", height: "297mm", marginBottom: 24 }}>
                            <DTRFWPPFormatToPrint ref={printRefContrat} contrat={contratDoc} />
                        </div>
                        <div ref={printRefAttestation} style={{ width: "297mm", height: "210mm" }}>
                            <AttestationMotoToPrint
                                engin={contrat?.engins?.[0]}
                                contrat={contratDoc}
                            />
                        </div>
                    </div>
                </Card>
                <Button
                    onClick={exportToOnePdf}
                    type="primary"
                    icon={<SendOutlined />}
                    loading={loadingPrinting}
                    block
                    style={{
                        ...buttonStyle,
                        background: accentColor,
                        borderColor: accentColor,
                        height: "48px"
                    }}
                    size="large"
                >
                    Envoyer les Documents
                </Button>
            </Modal>
        </>
    );
};

export default DetailsTab;
